import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '../../i18n';
import FHSelect from './FHSelect';
import { BackgroundTypes } from '../../constants';
import { FORTA_BLUE, FORTA_WHITE } from '../../utils/colors';

import type { RootState } from '../../store';
import type { SxProps, Theme } from '@mui/material';

type LanguageSwitcherVariant = 'floating' | 'inline';

type LanguageSwitcherProps = {
  variant?: LanguageSwitcherVariant;
  sx?: SxProps<Theme>;
};

const LanguageSwitcher = ({ variant = 'floating', sx }: LanguageSwitcherProps) => {
  const { i18n } = useTranslation('common');
  const { provider } = useSelector((state: RootState) => state.providers);
  const supportedLanguages = provider?.supported_languages ?? SUPPORTED_LANGUAGES;
  const currentLanguage =
    supportedLanguages.find((language) => language === i18n.resolvedLanguage) ?? DEFAULT_LANGUAGE;

  const isFloating = variant === 'floating';

  return (
    <Box
      sx={{
        ...(isFloating
          ? {
              position: 'fixed',
              top: 16,
              right: 16,
              zIndex: (theme) => theme.zIndex.modal + 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 0.5,
            }
          : {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }),
        ...sx,
      }}
    >
      <FHSelect
        value={currentLanguage}
        backgroundType={BackgroundTypes.Colored}
        sx={{ backgroundColor: 'transparent' }}
        options={SUPPORTED_LANGUAGES.map((language) => ({
          value: language,
          label:
            language === 'en' ? (
              <Box>
                <Typography
                  display="inline"
                  sx={{
                    px: 1,
                    color: FORTA_BLUE.primary,
                    fontWeight: 700,
                    fontSize: 18,
                    backgroundColor: FORTA_WHITE,
                    borderRadius: '4px',
                  }}
                >
                  En
                </Typography>
                <Typography display="inline" ml={1}>
                  English
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography
                  display="inline"
                  sx={{
                    px: 1,
                    color: FORTA_BLUE.primary,
                    fontWeight: 700,
                    fontSize: 18,
                    backgroundColor: FORTA_WHITE,
                    borderRadius: '4px',
                  }}
                >
                  Sp
                </Typography>
                <Typography display="inline" ml={1}>
                  Spanish
                </Typography>
              </Box>
            ),
        }))}
        renderValue={(value) => {
          return (
            <Box py={0.7}>
              <Typography
                sx={{
                  px: 1,
                  color: FORTA_BLUE.primary,
                  fontWeight: 700,
                  fontSize: 18,
                  backgroundColor: FORTA_WHITE,
                  borderRadius: '4px',
                }}
              >
                {value === 'en' ? 'En' : 'Sp'}
              </Typography>
            </Box>
          );
        }}
        onUpdate={(value) => i18n.changeLanguage(String(value))}
      />
    </Box>
  );
};

export default LanguageSwitcher;
